// 高中檢視紀錄:下載成績表 Excel / 學生成績單 zip(kanban 107190)
// 下載邏輯與檔名沿用國中 batch-records;資料與評分向度以高中既有為準。
import * as XLSX from "xlsx";
import JSZip from "jszip";
import {
  buildCorrectionResultBlob,
  type ImageData,
} from "./downloadResultsUtil";
import { triggerFileDownload, sanitizeFilename } from "./fileDownload";
import type { Record as ClassRecord } from "../types/common";
import { normalizeCorrection, orderedCategories } from "./correctionFormat";

// 高中批改結果的向度結構:{ [向度]: {等第, 得分, 評語}, 整體評分: {總分, 評語, 建議…} }
interface CategoryEntry {
  等第?: string;
  得分?: number | string;
  評語?: string;
}
interface OverallEntry {
  等第?: string;
  總分?: number | string;
  滿分?: number | string;
  評語?: string;
  建議?: string | string[];
}
type HsCorrectingResult = { [key: string]: CategoryEntry | OverallEntry | undefined };

// 是不是高中 25 分制那組向度。
// 原本直接看頂層有沒有「題旨發揮」鍵,新格式(kanban 1000517)把四構面收進
// categories 陣列、頂層沒有那個鍵,會被誤判成舊制那組 —— 欄位與取值全錯。
// 改走 normalizeCorrection,兩種格式都由它收斂成同一份 categories 再判。
const isHsScheme = (result: HsCorrectingResult): boolean =>
  normalizeCorrection(result)?.categories.some((c) => c.name === "題旨發揮") ??
  false;

// 與 ClassFeedback 的評分方案判斷一致(新制 25 分 4 向度 / 舊制)
export const getScoreCategories = (result: HsCorrectingResult): string[] =>
  isHsScheme(result)
    ? ["題旨發揮", "結構安排", "字句應用", "資料掌握"]
    : ["立意取材", "結構組織", "遣詞造句", "錯別字、格式與標點符號"];

// 成績單 docx 的向度順序照 PM 範例檔(2026-07-28 回饋):題旨發揮→資料掌握→結構安排→字句應用
// (成績表 Excel 欄序 PM 已確認 OK,維持 getScoreCategories 不動 —— 兩者順序
//  刻意不同,不要為了「統一」而改掉任一邊,兩個順序都是 PM 分別確認過的)
export const getReportCategories = (result: HsCorrectingResult): string[] =>
  isHsScheme(result)
    ? ["題旨發揮", "資料掌握", "結構安排", "字句應用"]
    : ["立意取材", "結構組織", "遣詞造句", "錯別字、格式與標點符號"];

const parseResult = (record: ClassRecord): HsCorrectingResult | null => {
  const raw = record.correctingResult;
  if (raw && typeof raw === "object") return raw as HsCorrectingResult;
  if (typeof raw === "string") {
    try {
      return JSON.parse(raw.replace(/```json\n|\n```/g, ""));
    } catch {
      return null;
    }
  }
  return null;
};

const bySeat = (a: ClassRecord, b: ClassRecord) =>
  (parseInt(a.studentSeatNo, 10) || 0) - (parseInt(b.studentSeatNo, 10) || 0);

// ── 成績表 Excel ─────────────────────────────────────────────────────────

export const downloadScoreExcel = async (params: {
  records: ClassRecord[];
  className: string;
  chapterName: string;
}): Promise<void> => {
  const { records, className, chapterName } = params;
  if (records.length === 0) {
    throw new Error("目前沒有可匯出的學生成績");
  }

  // 欄位序照 PM 範例檔(2026-07-22 回饋):座號|姓名|各向度|總分|上傳時間,無「狀態」欄
  const firstParsed = parseResult(records[0]) || {};
  const categories = getScoreCategories(firstParsed);
  const header = ["座號", "姓名", ...categories, "總分", "上傳時間"];

  const sorted = [...records].sort(bySeat);
  const rows = sorted.map((r) => {
    const normalized = normalizeCorrection(parseResult(r));
    const row: { [key: string]: string | number | Date } = {
      座號: r.studentSeatNo ?? "",
      姓名: r.studentName || "",
      總分: normalized?.totalScore ?? "",
      上傳時間: r.createdAt ? new Date(r.createdAt) : "",
    };
    // 取不到分數留空白(維持原行為):0 是合法分數,不能拿來表示「這格沒資料」
    categories.forEach((cat) => {
      row[cat] =
        normalized?.categories.find((c) => c.name === cat)?.score ?? "";
    });
    return row;
  });

  const ws = XLSX.utils.json_to_sheet(rows, { header, cellDates: true });
  // 欄寬同範例檔;上傳時間顯示 yyyy/m/d hh:mm
  ws["!cols"] = [
    { wch: 6.8 },
    { wch: 12.8 },
    ...categories.map(() => ({ wch: 10.8 })),
    { wch: 8.4 },
    { wch: 24.2 },
  ];
  const timeCol = header.indexOf("上傳時間");
  sorted.forEach((_, i) => {
    const cell = ws[XLSX.utils.encode_cell({ r: i + 1, c: timeCol })];
    if (cell && cell.v instanceof Date) cell.z = "yyyy/m/d hh:mm";
  });
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, "成績表");

  const buf = XLSX.write(wb, { bookType: "xlsx", type: "array", cellDates: true });
  const blob = new Blob([buf], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });
  await triggerFileDownload(
    blob,
    `${sanitizeFilename(className || "班級")}_${sanitizeFilename(chapterName || "作文")}_成績表.xlsx`,
    "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
  );
};

// ── 學生成績單 zip ────────────────────────────────────────────────────────

// 抓學生作文圖(校正後整頁優先);CORS 或抓取失敗回空陣列 → 改用 OCR 文字
const fetchStudentImages = async (record: ClassRecord): Promise<ImageData[]> => {
  const urlsRaw = record.studentInputOriginUrl || record.studentInputUrl || "";
  const urls = urlsRaw.split(",").map((u) => u.trim()).filter(Boolean);
  const images: ImageData[] = [];
  for (const url of urls) {
    try {
      const res = await fetch(url);
      if (!res.ok) throw new Error(String(res.status));
      const blob = await res.blob();
      const base64 = await new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(String(reader.result));
        reader.onerror = () => reject(reader.error);
        reader.readAsDataURL(blob);
      });
      images.push({ base64, mimeType: blob.type || "image/jpeg" });
    } catch {
      return []; // 任一張失敗就整體退回文字模式,避免缺頁誤導
    }
  }
  return images;
};

export const downloadReportZip = async (params: {
  records: ClassRecord[];
  className: string;
  chapterName: string;
  onProgress?: (done: number, total: number) => void;
}): Promise<void> => {
  const { records, className, chapterName, onProgress } = params;
  const sorted = [...records].sort(bySeat);
  if (sorted.length === 0) {
    throw new Error("目前沒有已完成批改的學生可下載");
  }

  const zip = new JSZip();
  let fileCount = 0;

  // 照 PM 範例檔(2026-07-28 回饋):zip 檔名與內層資料夾都是 {班級}_{題目}_成績表
  const reportBaseName = `${sanitizeFilename(className || "班級")}_${sanitizeFilename(chapterName || "作文")}_成績表`;
  const folder = zip.folder(reportBaseName)!;

  for (let i = 0; i < sorted.length; i++) {
    const record = sorted[i];
    const parsed = parseResult(record);
    if (!parsed) {
      onProgress?.(i + 1, sorted.length);
      continue;
    }
    const normalized = normalizeCorrection(parsed);
    const categories = orderedCategories(parsed, getReportCategories(parsed));
    const suggestions = normalized?.suggestions ?? [];

    const studentImages = await fetchStudentImages(record);

    const blob = await buildCorrectionResultBlob(
      {
        totalScore: normalized?.totalScore ?? 0,
        categories,
        overallComment: normalized?.overallComment ?? "",
        suggestions,
        essayTitle: chapterName,
        studentEssay: studentImages.length > 0 ? "" : record.studentInput || "",
        studentImages,
      },
      {
        seatNumber: record.studentSeatNo,
        studentName: record.studentName,
      }
    );

    // docx 檔名照範例:{座號}_{姓名}_{題目}_成績表;同座號同姓名重複紀錄時加序號避免檔名衝突
    const baseFileName = sanitizeFilename(
      `${record.studentSeatNo || ""}_${record.studentName || ""}_${chapterName || "作文"}_成績表`
    );
    let fileName = `${baseFileName}.docx`;
    let dupIndex = 1;
    while (folder.files[`${reportBaseName}/${fileName}`]) {
      dupIndex++;
      fileName = `${baseFileName}(${dupIndex}).docx`;
    }
    folder.file(fileName, blob);
    fileCount++;
    onProgress?.(i + 1, sorted.length);
  }

  if (fileCount === 0) {
    throw new Error("沒有成功產生任何批改結果檔案");
  }

  const zipBlob = await zip.generateAsync({ type: "blob" });
  await triggerFileDownload(zipBlob, `${reportBaseName}.zip`, "application/zip");
};
